<template>
	<div class="app-version-page">
		<div class="toolbar">
			<Button type="primary" icon="md-refresh" @click="loadList(true)">刷新</Button>
			<span class="hint">修改后请点「清除缓存」让 App 端立即生效（默认 60s 缓存）</span>
		</div>

		<Table border :columns="columns" :data="list" :loading="loading"></Table>

		<!-- 编辑弹窗 -->
		<Modal
			v-model="editModal"
			:title="form.platform ? '编辑版本配置 - ' + form.platform : '编辑版本配置'"
			@on-ok="onSave"
			@on-cancel="onCancelEdit"
			width="700"
		>
			<Form ref="formRef" :model="form" :label-width="120">
				<FormItem label="平台" prop="platform">
					<Input v-model="form.platform" disabled></Input>
				</FormItem>
				<FormItem label="版本号" prop="version">
					<Input v-model="form.version" placeholder="如 1.4.5（与 manifest.json versionName 一致）"></Input>
				</FormItem>
				<FormItem label="版本号(数字)" prop="version_code">
					<Input v-model="form.version_code" placeholder="如 145（与 manifest.json versionCode 一致）"></Input>
				</FormItem>
				<FormItem label="最低支持版本" prop="min_version">
					<Input v-model="form.min_version" placeholder="低于此版本强制更新"></Input>
				</FormItem>
				<FormItem label="下载类型">
					<Select v-model="form.file_type" style="width:200px">
						<Option value="wgt">wgt - 资源包（热更新）</Option>
						<Option value="apk">apk - 安装包</Option>
					</Select>
				</FormItem>
				<FormItem label="wgt URL">
					<Input v-model="form.wgt_path" placeholder="完整 URL，含 http/https，如 https://cdn.example.com/app/update/__UNI__.wgt"></Input>
				</FormItem>
				<FormItem label="apk URL">
					<Input v-model="form.apk_path" placeholder="完整 URL，含 http/https，如 https://cdn.example.com/app/download/app-v1.4.5.apk"></Input>
				</FormItem>
				<FormItem label="包大小">
					<Input v-model="form.size" placeholder="如 2.5MB"></Input>
				</FormItem>
				<FormItem label="更新说明">
					<Input v-model="form.note" type="textarea" :rows="3" placeholder="将展示在 App 更新弹窗里"></Input>
				</FormItem>
				<FormItem label="强制更新">
					<i-switch v-model="form.force_update_bool" size="large">
						<span slot="open">是</span>
						<span slot="close">否</span>
					</i-switch>
					<span class="form-hint">开启后所有低于此版本号的客户端必须更新才能用</span>
				</FormItem>
				<FormItem label="状态">
					<i-switch v-model="form.status_bool" size="large">
						<span slot="open">启用</span>
						<span slot="close">下线</span>
					</i-switch>
					<span class="form-hint">下线后该记录不再下发给 App，用于版本回滚</span>
				</FormItem>
			</Form>
		</Modal>
	</div>
</template>

<script>
	import {
		getAppVersionList,
		saveAppVersion,
		clearAppVersionCache,
	} from '@/api/admin'

	export default {
		name: 'app_version',
		data() {
			return {
				loading: false,
				list: [],
				editModal: false,
				form: this.emptyForm(),
				columns: [
					{ title: '平台', key: 'platform', align: 'center', width: 110 },
					{ title: '版本号', key: 'version', align: 'center', width: 110 },
					{ title: '版本号(数字)', key: 'version_code', align: 'center', width: 110 },
					{ title: '最低支持', key: 'min_version', align: 'center', width: 110 },
					{
						title: '下载类型', key: 'file_type', align: 'center', width: 110,
						render: (h, params) => h('span', params.row.file_type === 'apk' ? 'apk 安装包' : 'wgt 资源包'),
					},
					{ title: 'wgt URL', key: 'wgt_path', align: 'center', tooltip: true },
					{ title: 'apk URL', key: 'apk_path', align: 'center', tooltip: true },
					{ title: '包大小', key: 'size', align: 'center', width: 90 },
					{
						title: '强制更新', key: 'force_update', align: 'center', width: 90,
						render: (h, params) => h('span', { style: { color: params.row.force_update ? '#ed4014' : '#19be6b' } },
							params.row.force_update ? '是' : '否'),
					},
					{
						title: '状态', key: 'status', align: 'center', width: 80,
						render: (h, params) => h('Tag', { props: { color: params.row.status ? 'success' : 'default' } },
							params.row.status ? '启用' : '下线'),
					},
					{
						title: '操作', key: 'action', align: 'center', width: 220, fixed: 'right',
						render: (h, params) => {
							return h('div', [
								h('Button', {
									props: { type: 'primary', size: 'small' },
									style: { marginRight: '8px' },
									on: { click: () => this.openEdit(params.row) },
								}, '编辑'),
								h('Button', {
									props: { type: 'warning', size: 'small' },
									on: { click: () => this.onClearCache(params.row) },
								}, '清除缓存'),
							])
						},
					},
				],
			}
		},
		mounted() {
			this.loadList()
		},
		methods: {
			emptyForm() {
				return {
					platform: '',
					version: '',
					version_code: '',
					min_version: '1.0.0',
					wgt_path: '',
					apk_path: '',
					file_type: 'wgt',
					size: '',
					note: '',
					force_update_bool: false,
					status_bool: true,
				}
			},
			async loadList(showOk = false) {
				this.loading = true
				try {
					const res = await getAppVersionList(this)
					if (res && res.code === 200) {
						this.list = (res.data && res.data.list) || []
						if (showOk) this.$Message.success('已刷新')
					}
				} finally {
					this.loading = false
				}
			},
			openEdit(row) {
				this.form = {
					platform: row.platform,
					version: row.version || '',
					version_code: row.version_code || '',
					min_version: row.min_version || '1.0.0',
					wgt_path: row.wgt_path || '',
					apk_path: row.apk_path || '',
					file_type: row.file_type || 'wgt',
					size: row.size || '',
					note: row.note || '',
					force_update_bool: !!row.force_update,
					status_bool: !!row.status,
				}
				this.editModal = true
			},
			onCancelEdit() {
				this.form = this.emptyForm()
			},
			async onSave() {
				const f = this.form
				if (!f.version || !f.version_code) {
					this.$Message.error('版本号 和 版本号(数字) 必填')
					return
				}
				if (f.file_type === 'wgt' && !f.wgt_path) {
					this.$Message.error('当前下载类型为 wgt，请填写 wgt URL')
					return
				}
				if (f.file_type === 'apk' && !f.apk_path) {
					this.$Message.error('当前下载类型为 apk，请填写 apk URL')
					return
				}
				const data = {
					platform: f.platform,
					version: f.version,
					version_code: f.version_code,
					min_version: f.min_version,
					wgt_path: f.wgt_path,
					apk_path: f.apk_path,
					file_type: f.file_type,
					size: f.size,
					note: f.note,
					force_update: f.force_update_bool ? 1 : 0,
					status: f.status_bool ? 1 : 0,
				}
				const res = await saveAppVersion(this, data)
				if (res && res.code === 200) {
					this.$Message.success('保存成功（缓存已自动清理）')
					this.editModal = false
					this.loadList()
				}
			},
			async onClearCache(row) {
				this.$Modal.confirm({
					title: '清除缓存',
					content: `确认清除「${row.platform}」平台的版本配置缓存？清除后 App 下次请求会拉取最新配置`,
					onOk: async () => {
						const res = await clearAppVersionCache(this, { platform: row.platform })
						if (res && res.code === 200) {
							this.$Message.success('缓存已清除')
						}
					},
				})
			},
		},
	}
</script>

<style scoped>
.app-version-page {
	padding: 16px;
	background: #fff;
	min-height: calc(100vh - 60px);
}
.toolbar {
	display: flex;
	align-items: center;
	margin-bottom: 16px;
}
.hint {
	margin-left: 16px;
	color: #999;
	font-size: 12px;
}
.form-hint {
	margin-left: 12px;
	color: #999;
	font-size: 12px;
}
</style>
